import { KeyValuePair } from '@acp/core-ui';
import { ContactNumber, formatContactNumber, getReadableContactNumberType } from '@acp/shared';
import {
    Card, CardBody, Grid, GridItem, Text,
} from '@chakra-ui/react';

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[`
                "name name"
                "email email"
                "contactNumbers contactNumbers"
            `,
            null,
            `
                "name name"
                "email contactNumbers"
            `]}
            gridTemplateColumns={['1fr 1fr', null, '5fr 3fr']}
            gap={6}
            alignItems="start"
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

function GridWrapperWithActions({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[`
                "name name"
                "email email"
                "contactNumbers contactNumbers"
                "actions actions"
            `,
            null,
            `
                "name actions"
                "email contactNumbers"
            `]}
            gridTemplateColumns={['1fr 1fr', null, '5fr 3fr']}
            gap={6}
            alignItems="start"
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

type OccupantCardContactNumber = Omit<ContactNumber, 'doNotContactReasonCode' | 'doNotContactReasonName' | 'doNotContactReasonNotes' | 'notAllowedToContact'>;

function OccupantCard({
    name,
    email,
    contactNumbers,
    showActions = false,
    children,
}: {
    name: string | null;
    email: string | null;
    contactNumbers: OccupantCardContactNumber[];
    showActions?: boolean;
    children?: React.ReactNode;
}) {
    return (
        <Card
            borderColor="muted.50"
            borderWidth={1}
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            <CardBody
                as={showActions ? GridWrapperWithActions : GridWrapper}
                p={0}
                alignItems="start"
                justifyItems="center"
            >
                <GridItem area="name">
                    <Text fontWeight="bold">{name}</Text>
                </GridItem>
                <GridItem area="email">
                    <KeyValuePair label="Email">
                        {
                            email !== null && email !== ''
                                ? <Text>{email}</Text>
                                : <Text color="muted.300">Not specified</Text>
                        }
                    </KeyValuePair>
                </GridItem>
                <GridItem area="contactNumbers">
                    <KeyValuePair label="Contact numbers">
                        {
                            contactNumbers.length > 0
                                ? contactNumbers.map((contactNumber, id) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <Text key={id}>
                                        {formatContactNumber(contactNumber.number)}
                                        <Text as="span" color="muted.500" pl={1}>
                                            {`(${getReadableContactNumberType(contactNumber.type)})`}
                                        </Text>
                                    </Text>
                                ))
                                : <Text color="muted.300">None</Text>
                        }
                    </KeyValuePair>
                </GridItem>
                {
                    showActions && (
                        <GridItem area="actions" alignSelf="flex-start" justifySelf={['flex-start', null, 'flex-end']}>
                            {children}
                        </GridItem>
                    )
                }
            </CardBody>
        </Card>
    );
}

export { OccupantCard };
